import React from "react";
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import {
  AUTHENTICATED_INACTIVE_COMPANY_ENTRY,
} from "configs/AppConfig";
import {
  inactiveCompanyRoutes,
  protectedRoutes,
  publicRoutes,
  brandStoreRoutes,
  catalogRoutes,
  agencyRoutes,
  onboardingRoutes,
} from "configs/RoutesConfig";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import AppRoute from "./AppRoute";
import { useSelector } from "react-redux";
import {
  getAuthenticatedEntry,
  hasBrandStoreAccess,
  hasCatalogAccess,
  hasRoleAccess,
  hasWardrobeAccess,
  hasAgencyAccess,
  hasRequestFormsAccess,
  hasCustomShipmentAccess,
  isNexoCompany,
} from "../utils/accessControl";

const Routes = () => {
  const { token, user } = useSelector((state) => state.auth);
  const isCompanyActive =
    user?.current_company?.is_active || Boolean(user?.is_sunday_super_admin);

  // Get dynamic authenticated entry based on user access flags
  const authenticatedEntry = getAuthenticatedEntry(user);

  // Check user access flags
  const userHasBrandStoreAccess = hasBrandStoreAccess(user);
  const userHasCatalogAccess = hasCatalogAccess(user);
  const userHasWardrobeAccess = hasWardrobeAccess(user);

  // Filter protected routes based on access
  let availableProtectedRoutes = isCompanyActive
    ? protectedRoutes
    : inactiveCompanyRoutes;

  // If user doesn't have wardrobe access, filter out wardrobe-specific routes
  if (!userHasWardrobeAccess && isCompanyActive) {
    availableProtectedRoutes = availableProtectedRoutes.filter(
      (route) => !route.meta?.isWardrobe
    );
  }

  // Nexo-only internal tooling (campaign burner links). Gated here as well as in
  // the nav, otherwise the page stays reachable by typing the URL.
  if (!isNexoCompany(user)) {
    availableProtectedRoutes = availableProtectedRoutes.filter(
      (route) => !route.meta?.requiresNexoCompany
    );
  }

  // If user doesn't have request-forms access (limited rollout), filter out
  // the admin request-forms routes. The public pages (/request-forms?token=...,
  // /internal-request) don't carry this meta flag and stay open.
  if (!hasRequestFormsAccess(user)) {
    availableProtectedRoutes = availableProtectedRoutes.filter(
      (route) => !route.meta?.requiresRequestFormsAccess
    );
  }

  // Custom shipment CREATION is a user-only gate. When off, drop the create route
  // so typing/bookmarking the URL falls through to the catch-all redirect below.
  // Editing existing custom shipments is NOT gated (that route lacks this meta).
  if (!hasCustomShipmentAccess(user)) {
    availableProtectedRoutes = availableProtectedRoutes.filter(
      (route) => !route.meta?.requiresCustomShipmentAccess
    );
  }

  // Filter out routes that require specific roles
  availableProtectedRoutes = availableProtectedRoutes.filter(
    (route) => hasRoleAccess(user, route.meta?.requiredRoles, route.meta?.allowClosetAdmin)
  );

  // Filter brand store routes based on access
  const availableBrandStoreRoutes = userHasBrandStoreAccess
    ? brandStoreRoutes
    : [];

  return (
    <RouterRoutes>
      <Route path="/" element={<ProtectedRoute />}>
        <Route
          path="/"
          element={
            <Navigate
              replace
              to={
                isCompanyActive
                  ? authenticatedEntry
                  : AUTHENTICATED_INACTIVE_COMPANY_ENTRY
              }
            />
          }
        />
        {!!token &&
          availableProtectedRoutes.map((route, index) => {
            return (
              <Route
                key={route.key + index}
                path={route.path}
                element={
                  <AppRoute
                    routeKey={route.key}
                    component={route.component}
                    {...route.meta}
                  />
                }
              />
            );
          })}
        {!!token &&
          availableBrandStoreRoutes.map((route, index) => {
            return (
              <Route
                key={route.key + index}
                path={route.path}
                element={
                  <AppRoute
                    routeKey={route.key}
                    component={route.component}
                    {...route.meta}
                  />
                }
              />
            );
          })}
        {!!token &&
          userHasCatalogAccess &&
          catalogRoutes.map((route, index) => {
            return (
              <Route
                key={route.key + index}
                path={route.path}
                element={
                  <AppRoute
                    routeKey={route.key}
                    component={route.component}
                    {...route.meta}
                  />
                }
              />
            );
          })}
        {!!token &&
          hasAgencyAccess(user) &&
          agencyRoutes.map((route, index) => {
            return (
              <Route
                key={route.key + index}
                path={route.path}
                element={
                  <AppRoute
                    routeKey={route.key}
                    component={route.component}
                    {...route.meta}
                  />
                }
              />
            );
          })}
        {!!token &&
          onboardingRoutes.map((route, index) => {
            return (
              <Route
                key={route.key + index}
                path={route.path}
                element={
                  <AppRoute
                    routeKey={route.key}
                    component={route.component}
                    {...route.meta}
                  />
                }
              />
            );
          })}
        {/* Dynamic fallback based on user access */}
        <Route path="*" element={<Navigate to={authenticatedEntry} replace />} />
      </Route>

      <Route element={<PublicRoute />}>
        {!token &&
          publicRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <AppRoute
                  routeKey={route.key}
                  component={route.component}
                  {...route.meta}
                />
              }
            />
          ))}
      </Route>
    </RouterRoutes>
  );
};

export default Routes;
